// =============================================================================
// Setup view logic — ported from SimSense (osc-telemetry SetupDiffViewer).
// Pure functions: groups a raw iRacing CarSetup into corner pairs + shared
// items, humanizes keys, and converts iRacing's metric display strings to US
// standard. Rendering (re-skinned to Pitwall Editorial) lives in the component.
//
// Unit conversion is SINGLE-SNAP by construction: it parses the number iRacing
// already printed at full precision, converts once, and snaps once at the end —
// it never re-rounds the metric value first (the double-rounding trap).
// =============================================================================

export type UnitSystem = "standard" | "metric";

export const IRRELEVANT_METADATA = [
  "UpdateCount",
  "Name",
  "version",
  "carName",
  "Update count",
  "SteeringRatio",
];

export function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/\b(Ls|Hs)\b/g, (s) => s.toUpperCase()) // LS/HS shock prefixes
    .replace(/\bArb\b/g, "ARB") // anti-roll bar
    .trim();
}

/**
 * Convert one iRacing display string (e.g. "138 kPa", "118 N/mm", "134.5 mm")
 * to the requested unit system. Key-aware so each parameter snaps to the
 * iRacing garage increment (psi 0.5, N/mm 25, toe x/32", ARB 1/16", ...).
 * Returns the string unchanged when no convertible unit is present.
 */
export function convertSetupString(str: string, system: UnitSystem, key = ""): string {
  if (/\d+C\b/.test(str) && system === "standard") {
    return str.replace(/(\d+(?:\.\d+)?)C\b/g, (_, n) => `${Math.round(parseFloat(n) * 9 / 5 + 32)}F`);
  }
  if (/\d+F\b/.test(str) && system === "metric") {
    return str.replace(/(\d+(?:\.\d+)?)F\b/g, (_, n) => `${Math.round((parseFloat(n) - 32) * 5 / 9)}C`);
  }
  if (/\d+\s*kPa/.test(str) && system === "standard") {
    return str.replace(/(\d+(?:\.\d+)?)\s*kPa/g, (_, n) => {
      const psi = parseFloat(n) * 0.145038;
      const lk = key.toLowerCase();
      // Cold pressures snap to 0.5 psi increments.
      if (lk.includes("coldpress") || lk.includes("cold")) {
        return `${(Math.round(psi * 2) / 2).toFixed(1)} psi`;
      }
      return `${psi.toFixed(1)} psi`;
    });
  }
  if (/\d+\s*kPa/.test(str) && system === "metric") {
    return str; // already metric
  }
  // N/mm -> lbs/in, snapped to the nearest 25 lbs/in garage increment.
  if (/\d+\s*N\/mm/.test(str) && system === "standard") {
    return str.replace(/(\d+(?:\.\d+)?)\s*N\/mm/g, (_, n) => {
      const lbsIn = parseFloat(n) * 5.71015;
      return `${Math.round(lbsIn / 25) * 25} lbs/in`;
    });
  }
  if (/\d+(?:\.\d+)?\s*Nm/.test(str) && system === "standard") {
    return str.replace(/(\d+(?:\.\d+)?)\s*Nm/g, (_, n) => `${(parseFloat(n) * 0.737562).toFixed(1)} ft-lbs`);
  }
  if (/\d+\s*N\b/.test(str) && system === "standard") {
    return str.replace(/(\d+(?:\.\d+)?)\s*N\b/g, (_, n) => `${Math.round(parseFloat(n) * 0.224809)} lbs`);
  }
  if (/\d+\s*N\b/.test(str) && system === "metric") {
    return str.replace(/(\d+(?:\.\d+)?)\s*N\b/g, (_, n) => `${Math.round(parseFloat(n) / 9.80665)} kg`);
  }
  // mm -> in with key-aware rounding.
  if (/[+-]?\d+(?:\.\d+)?\s*mm/.test(str) && system === "standard") {
    return str.replace(/([+-]?\d+(?:\.\d+)?)\s*mm/g, (_, n) => {
      const inches = parseFloat(n) / 25.4;
      const lk = key.toLowerCase();
      // Ballast snaps to 0.5" increments.
      if (lk.includes("ballast")) {
        const snapped = Math.round(inches * 2) / 2;
        return `${snapped < 0 ? "" : "+"}${snapped.toFixed(1)} in`;
      }
      // Link slack as unsigned x/16".
      if (lk.includes("slack") || lk.includes("link")) {
        return `${Math.round(Math.abs(inches) * 16)}/16"`;
      }
      // Toe-in as signed x/32".
      if (lk.includes("toe")) {
        const thirtySeconds = Math.round(inches * 32);
        return `${thirtySeconds >= 0 ? "+" : ""}${thirtySeconds}/32"`;
      }
      // ARB diameter snaps to 1/16" increments.
      if (lk.includes("diameter")) {
        return `${(Math.round(inches * 16) / 16).toFixed(3)} in`;
      }
      // Trackbar snaps to 0.250" increments.
      if (lk.includes("trackbar")) {
        const snapped = Math.round(inches / 0.25) * 0.25;
        return `${snapped >= 0 ? "+" : ""}${snapped.toFixed(3)} in`;
      }
      return `${inches.toFixed(3)} in`;
    });
  }
  return str;
}

/** Render a single leaf value: convert unit strings, format raw numbers,
 *  and show 0/1 attach flags as checkboxes. */
export function renderSetupValue(key: string, val: unknown, system: UnitSystem): string {
  if (val === null || val === undefined) return "—";
  if (typeof val === "string") return convertSetupString(val, system, key);
  const upperKey = key.toUpperCase();
  if (typeof val === "number") {
    if (upperKey.includes("STEERINGRATIO")) return String(val);
    if ((val === 0 || val === 1) && (upperKey.includes("ATTACH") || upperKey.includes("LOCKED") || upperKey.includes("ENABLED"))) {
      return val === 1 ? "☑" : "☐";
    }
    if (upperKey.includes("TEMP")) return formatTemp(val, system);
    if (upperKey.includes("PRESS")) return formatPressure(val, system);
    if (upperKey.includes("WEIGHT") || upperKey.includes("RATE")) return formatWeight(val, system);
    return String(val);
  }
  return String(val);
}

// Numeric fallbacks for the rare leaf iRacing stores as a raw number (no unit
// in the string). Same conversions as the string path.
function formatTemp(c: number, system: UnitSystem): string {
  return system === "metric" ? `${Math.round(c)}°C` : `${Math.round((c * 9) / 5 + 32)}°F`;
}
function formatPressure(kpa: number, system: UnitSystem): string {
  return system === "metric" ? `${(kpa / 100).toFixed(2)} bar` : `${(Math.round(kpa * 0.145038 * 2) / 2).toFixed(1)} psi`;
}
function formatWeight(newtons: number, system: UnitSystem): string {
  return system === "metric" ? `${Math.round(newtons / 9.80665)} kg` : `${Math.round(newtons * 0.224809)} lbs`;
}

export interface SetupItem {
  flatKey: string;
  displayKey: string;
  value: unknown;
}
export interface SetupPair {
  label: string;
  left: { label: string; items: SetupItem[] };
  right: { label: string; items: SetupItem[] };
}
export interface SetupSectionData {
  name: string;
  pairs: SetupPair[];
  shared: SetupItem[];
}

const POSITION_KEYS = ["LeftFront", "RightFront", "LeftRear", "RightRear"];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Flatten a corner sub-object (e.g. Chassis.LeftFront) into display items. */
function flattenSub(obj: unknown, prefix: string): SetupItem[] {
  if (!isRecord(obj)) return [];
  const items: SetupItem[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (IRRELEVANT_METADATA.includes(k)) continue;
    const flatKey = `${prefix}_${k}`;
    if (isRecord(v)) {
      for (const [sk, sv] of Object.entries(v)) {
        items.push({ flatKey: `${flatKey}_${sk}`, displayKey: humanizeKey(sk), value: sv });
      }
    } else {
      items.push({ flatKey, displayKey: humanizeKey(k), value: v });
    }
  }
  return items;
}

/**
 * Group a raw CarSetup into renderable sections. Top-level keys become
 * sections; LF/RF/LR/RR sub-keys become corner pairs (read across corners);
 * other sub-keys become shared items. Post-processing splits the Chassis
 * section's shared front/rear settings into "Front End" and "Rear Settings"
 * cards, matching the iRacing garage mental model.
 */
export function buildSetupSections(setup: unknown): SetupSectionData[] {
  if (!isRecord(setup)) return [];
  const sections: SetupSectionData[] = [];
  const generalItems: SetupItem[] = [];

  for (const [topKey, topVal] of Object.entries(setup)) {
    if (IRRELEVANT_METADATA.includes(topKey)) continue;
    if (!isRecord(topVal)) {
      generalItems.push({ flatKey: topKey, displayKey: humanizeKey(topKey), value: topVal });
      continue;
    }
    const sub = topVal;
    const hasPositions = Object.keys(sub).some((k) => POSITION_KEYS.includes(k));
    const section: SetupSectionData = { name: humanizeKey(topKey), pairs: [], shared: [] };

    if (hasPositions) {
      if (sub.LeftFront || sub.RightFront) {
        section.pairs.push({
          label: "Front",
          left: { label: "LF", items: flattenSub(sub.LeftFront, `${topKey}_LeftFront`) },
          right: { label: "RF", items: flattenSub(sub.RightFront, `${topKey}_RightFront`) },
        });
      }
      if (sub.LeftRear || sub.RightRear) {
        section.pairs.push({
          label: "Rear",
          left: { label: "LR", items: flattenSub(sub.LeftRear, `${topKey}_LeftRear`) },
          right: { label: "RR", items: flattenSub(sub.RightRear, `${topKey}_RightRear`) },
        });
      }
      for (const [subKey, subVal] of Object.entries(sub)) {
        if (POSITION_KEYS.includes(subKey)) continue;
        if (isRecord(subVal)) {
          for (const [k, v] of Object.entries(subVal)) {
            if (IRRELEVANT_METADATA.includes(k)) continue;
            section.shared.push({ flatKey: `${topKey}_${subKey}_${k}`, displayKey: humanizeKey(k), value: v });
          }
        } else {
          section.shared.push({ flatKey: `${topKey}_${subKey}`, displayKey: humanizeKey(subKey), value: subVal });
        }
      }
    } else {
      for (const [k, v] of Object.entries(sub)) {
        if (IRRELEVANT_METADATA.includes(k)) continue;
        if (isRecord(v)) {
          for (const [sk, sv] of Object.entries(v)) {
            section.shared.push({ flatKey: `${topKey}_${k}_${sk}`, displayKey: `${humanizeKey(k)}: ${humanizeKey(sk)}`, value: sv });
          }
        } else {
          section.shared.push({ flatKey: `${topKey}_${k}`, displayKey: humanizeKey(k), value: v });
        }
      }
    }
    if (section.pairs.length > 0 || section.shared.length > 0) sections.push(section);
  }
  if (generalItems.length > 0) sections.push({ name: "General", pairs: [], shared: generalItems });

  // Split Chassis shared items into Front End / Rear Settings cards.
  const tireIdx = sections.findIndex((s) => s.name.toLowerCase().includes("tire"));
  const chassisIdx = sections.findIndex((s) => s.name.toLowerCase().includes("chassis"));
  if (tireIdx >= 0 && chassisIdx >= 0) {
    const chassis = sections[chassisIdx];
    if (chassis.shared.length > 0 && chassis.pairs.length > 0) {
      const frontItems: SetupItem[] = [];
      const rearItems: SetupItem[] = [];
      for (const item of chassis.shared) {
        const lk = item.flatKey.toLowerCase();
        if (lk.includes("_rear_") && !lk.includes("frontarb")) rearItems.push(item);
        else frontItems.push(item);
      }
      chassis.shared = [];
      if (frontItems.length > 0) sections.splice(tireIdx + 1, 0, { name: "Front End", pairs: [], shared: frontItems });
      if (rearItems.length > 0) sections.push({ name: "Rear Settings", pairs: [], shared: rearItems });
    }
  }

  // Sort corner items into the garage order a crew chief reads.
  const CORNER_ORDER = [
    "corner weight", "ride height", "spring perch offset", "spring rate",
    "spring angle", "ls compression", "hs comp slope", "ls rebound",
    "hs rebound slope", "camber", "caster", "toe in",
  ];
  const orderIdx = (dk: string, order: string[]): number => {
    const lk = dk.toLowerCase();
    for (let i = 0; i < order.length; i++) {
      if (lk === order[i] || lk.endsWith(order[i]) || lk.includes(order[i])) return i;
    }
    return order.length;
  };
  const chassisSect = sections.find((s) => s.name.toLowerCase().includes("chassis"));
  if (chassisSect) {
    for (const pair of chassisSect.pairs) {
      pair.left.items.sort((a, b) => orderIdx(a.displayKey, CORNER_ORDER) - orderIdx(b.displayKey, CORNER_ORDER));
      pair.right.items.sort((a, b) => orderIdx(a.displayKey, CORNER_ORDER) - orderIdx(b.displayKey, CORNER_ORDER));
    }
  }

  // Split Front End into Front / Front ARB columns.
  const frontEndSect = sections.find((s) => s.name === "Front End");
  if (frontEndSect && frontEndSect.shared.length > 0) {
    const FRONT_ORDER = ["ballast forward", "nose weight", "cross weight", "steering offset", "front brake bias", "tape configuration"];
    const ARB_ORDER = ["diameter", "arm asymmetry", "link slack", "preload", "attach"];
    const frontItems: SetupItem[] = [];
    const arbItems: SetupItem[] = [];
    for (const item of frontEndSect.shared) {
      const lk = item.flatKey.toLowerCase();
      if (lk.includes("frontarb") || lk.includes("front_arb")) arbItems.push(item);
      else frontItems.push(item);
    }
    frontItems.sort((a, b) => orderIdx(a.displayKey, FRONT_ORDER) - orderIdx(b.displayKey, FRONT_ORDER));
    arbItems.sort((a, b) => orderIdx(a.displayKey, ARB_ORDER) - orderIdx(b.displayKey, ARB_ORDER));
    frontEndSect.shared = [];
    frontEndSect.pairs = [{
      label: "",
      left: { label: "Front", items: frontItems },
      right: { label: "Front ARB", items: arbItems },
    }];
  }

  // Reorder Rear Settings.
  const rearSect = sections.find((s) => s.name === "Rear Settings");
  if (rearSect && rearSect.shared.length > 0) {
    const REAR_ORDER = ["rear end ratio", "arb diameter", "diameter", "preload", "attach"];
    rearSect.shared.sort((a, b) => orderIdx(a.displayKey, REAR_ORDER) - orderIdx(b.displayKey, REAR_ORDER));
  }

  return sections;
}
